import adapters from './adapters'
import { calculateBMI } from '../utils'
const {
  mixpanel,
  ga,
  optimove,
  pinterest,
  taboola,
  facebook,
  outbrain,
  revcontent,
} = adapters
import { getItem } from 'js/storeManager'
import { BE_HEALTHIER, LOSE_WEIGHT, GET_STRONGER } from 'js/goals'

const goalTypes = {
  [BE_HEALTHIER]: 'Maintain',
  [LOSE_WEIGHT]: 'Lose',
  [GET_STRONGER]: 'Gain',
}

export const flowNames = [
  'My Account',
  'Gift Voucher',
  'Partner',
  'CRM',
  'Verify Email',
  'Article',
  'Regular',
]

export const trackStartRegistration = ({ entryPoint, flowName } = {}) => {
  if (!flowNames.includes(flowName)) {
    console.warn(`"${flowName}" is not a known flow name`)
  }

  mixpanel.track('Start Registration', {
    'Entry Point': entryPoint,
    'Flow Name': flowName,
  })
  ga.track('Start Registration')
  optimove.track('start_registration', { entry_point: entryPoint })
}

export const trackReadMoreInHero = () => {
  ga.track('Click on "Read more" in hero', {
    hitType: 'event',
    eventCategory: 'New startpage',
    eventLabel: 'Click on "Read more" in hero',
    eventValue: 0,
  })
}

export const trackStartVideo = () => {
  ga.track('Started video', {
    eventCategory: 'New startpage',
    eventLabel: 'Started video',
    eventValue: 0,
  })
}

export const trackStartRewatchVideo = () => {
  ga.track('Started rewatching video', {
    eventCategory: 'New startpage',
    eventLabel: 'Started rewatching video',
    eventValue: 0,
  })
}

export const trackRequestNewPassword = () => {
  ga.track('Request New Password')
  mixpanel.track('Request New Password')
  optimove.track('request_new_password')
}

export const trackChooseLoginMethod = ({ method: login_method }) => {
  ga.track('Choose Login Method', { 'Login Method': login_method })
  mixpanel.track('Choose Login Method', { 'Login Method': login_method })
  optimove.track('choose_login_method', { login_method })
}

export const trackLogin = ({ method: login_method, user }) => {
  const account_type = user.isPremium ? 'Paid' : 'Free'
  ga.track('Log In', { 'Login Method': login_method })
  mixpanel.track('Log In', { 'Login Method': login_method })

  if (user.id) {
    optimove.user({ id: String(user.id) })
  }

  if (login_method === 'Email') {
    mixpanel.people({
      userId: user.id,
      userProperties: {
        'Account Type': account_type,
      },
    })
  }

  optimove.track('log_in', {
    goal_type: goalTypes[user.goal],
    first_name: user.firstName,
    gender: user.gender === '1' ? 'Male' : 'Female',
    age: parseInt(user.age),
    weight: parseInt(user.weight),
    height: parseInt(user.height),
    goal_weight: user.goalWeight && parseInt(user.goalWeight),
    bmi: calculateBMI(user.weight, user.height),
    goal_bmi: calculateBMI(user.goalWeight, user.height),
    marketing_consent: true,
    email_validated: false,
    account_type,
    subscription_duration: user.purchaseType,
    login_method,
    user_id: user.id,
  })
}

export const trackLogOut = () => {
  ga.track('Log out')
  mixpanel.track('Log out')
  optimove.track('log_out')
}

export const trackLimitedLogin = id => {
  optimove.user({ id })
}

export const trackStartLogin = ({ entryPoint = '' } = {}) => {
  ga.track('Start Login', { 'Entry Point': entryPoint })
  mixpanel.track('Start Login', { 'Entry Point': entryPoint })
  optimove.track('start_login')
}

export const trackForgotPassword = () => {
  ga.track("Click 'Forgot Password'")
  mixpanel.track("Click 'Forgot Password'")
  optimove.track('click_forgot_password')
}

export const trackSelectPremiumSubscription = ({
  duration,
  discount = 0,
  flowName = '',
  coupon = '',
  entryPoint = '',
  language,
  country,
}) => {
  const props = {
    'Subscription Duration': duration,
    'Discount %': discount,
    'Entry Point': entryPoint,
    'Coupon Name': coupon,
    'Flow Name': flowName,
  }
  ga.track('Select Premium Subscription', props)
  mixpanel.track('Select Premium Subscription', props)
  optimove.track('select_premium_subscription', {
    subscription_duration: String(duration),
    redeem_flow: !!flowName,
    discount_percentage: discount,
    entry_point: entryPoint,
    redeem_flow_name: flowName,
    app_language: language,
    country,
  })
}

export const trackCompleteRegistration = ({
  profile,
  type,
  duration,
  flowName,
  method,
  language,
  country,
}) => {
  const userId = String(profile.id)
  const goalType = goalTypes[profile.goal]
  const gender = profile.gender === '1' ? 'Male' : 'Female'
  const age = parseInt(profile.age)
  const weight = parseInt(profile.weight)
  const height = parseInt(profile.height)
  const goalWeight = profile.goalWeight && parseInt(profile.goalWeight)
  const bmi = calculateBMI(profile.weight, profile.height)
  const goalBmi = calculateBMI(profile.goalWeight, profile.height)

  ga.track('Complete Registration')
  mixpanel.track('Complete Registration', {
    'Account Type': type,
    'Flow Name': flowName,
  })

  mixpanel.alias({ userId })

  mixpanel.people({
    userProperties: {
      'Goal Type': goalType,
      Gender: gender,
      Age: age,
      Weight: weight,
      Height: height,
      'Registration Date': new Date().toISOString(),
      'Registration Method': method,
      'Goal Weight': goalWeight,
      BMI: bmi,
      'Goal BMI': goalBmi,
      'Marketing Consent': true,
      'Email Validated': false,
      'Account Type': type,
      'User ID': userId,
      'Subscription Duration': duration,
    },
  })
  mixpanel.register({
    superProperties: {
      'Account Type': 'Free',
      'User ID': userId,
      'Subscription Duration': duration,
    },
  })

  optimove.user({
    id: userId,
    email: profile.email,
    eventName: 'signup',
    parameters: {
      source: 'create account',
    },
  })

  optimove.track('complete_registration', {
    goal_type: goalType,
    first_name: profile.firstName,
    gender,
    age,
    weight,
    height,
    registration_method: method,
    goal_weight: goalWeight,
    bmi,
    goal_bmi: goalBmi,
    marketing_consent: true,
    email_validated: false,
    account_type: type,
    user_id: userId,
    app_language: language,
    country,
  })

  facebook.track('CompleteRegistration')
}

export const trackRegistrationMethod = ({ method }) => {
  const props = { 'Registration Method': method }
  ga.track('Choose Registration Method', props)
  mixpanel.track('Choose Registration Method', props)
  optimove.track('choose_registration_method', { registration_method: method })
}

export const trackSelectGender = ({ gender, flowName }) => {
  const props = {
    Gender: gender,
    'Flow Name': flowName,
  }
  ga.track('Select Gender', props)
  mixpanel.track('Select Gender', props)
  optimove.track('select_gender', {
    gender,
  })
}

export const trackSelectGoal = ({ goal, flowName }) => {
  const props = {
    'Goal Type': goal,
    'Flow Name': flowName,
  }
  ga.track('Select Goal', props)
  mixpanel.track('Select Goal', props)
  optimove.track('select_goal', { goal_type: goal })
}

export const trackEnterBasicInformation = ({
  age,
  weight,
  height,
  flowName,
}) => {
  const props = {
    Age: age,
    Weight: weight,
    Height: height,
    'Flow Name': flowName,
  }
  ga.track('Enter Basic Information', props)
  mixpanel.track('Enter Basic Information', props)
  optimove.track('enter_basic_information', {
    age,
    weight,
    height,
  })
}

export const trackViewLandingPage = ({
  entryPoint,
  partner,
  flowName,
  coupon,
}) => {
  const props = {
    'Entry Point': entryPoint,
    'Partner Name': partner,
    'Flow Name': flowName,
    'Coupon Name': coupon,
  }
  ga.track('View Landing Page', props)
  mixpanel.track('View Landing Page', props)
  optimove.track('view_landing_page', {
    coupon_name: String(coupon),
    flow_name: String(flowName),
  })
}

export const trackInitiateDeleteAccount = ({ firstName = '' }) => {
  optimove.track('initiate_delete_account', {
    initiate_delete_account: true,
    first_name: firstName, // NOTE: should be User Property if possible
  })
}

export const trackEnterCoupon = ({ coupon, language, country }) => {
  const props = {
    'Coupon Name': coupon,
  }
  ga.track('Enter Code', props)
  mixpanel.track('Enter Code', props)
  optimove.track('enter_code', {
    code_name: coupon,
    app_language: language,
    country,
  })
}

export const trackErrorMessage = ({
  code: error_code,
  type: error_type,
  category: error_category,
}) => {
  optimove.track('receive_error', {
    error_code,
    error_type,
    error_category,
  })
}

export const trackViewFaq = () => {
  optimove.track('view_faq')
}

export const register = ({ superProperties }) => {
  mixpanel.register(superProperties)
}

export const reset = () => {
  mixpanel.reset()
}

export const trackPageView = category => {
  ga.page({
    url: document.location.href,
    pathname: document.location.pathname,
    title: document.title,
  })
  pinterest.page()
  optimove.page({
    url: document.location.href,
    title: document.title,
    language: document.documentElement.lang,
    category,
  })
  taboola.page()
  facebook.page()
  outbrain.page()
}

export const trackStartPlanTest = () => {
  ga.track('Started taking the test', {
    eventCategory: 'Plan test',
  })
}

export const trackFinishedPlanTest = () => {
  ga.track('Finished the test', {
    eventCategory: 'Plan test',
  })
}

export const trackClickToSeeSubscriptions = () => {
  optimove.track('click_to_see_subscriptions')
  ga.track('Click', {
    eventCategory: 'Upgrade',
    eventLabel: 'Clicked on "See plans" on verify page',
    eventValue: 0,
  })
  mixpanel.track('Click To See Plans')
}

export const trackCompletedPayment = eventProperties => {
  const mgidPostbackUrl = getItem('mgid_postback')
  mgidPostbackUrl && fetch(mgidPostbackUrl)
  const taboolaPostbackUrl = getItem('taboola_postback')
  taboolaPostbackUrl && fetch(taboolaPostbackUrl)
  facebook.track('Purchase', {
    currency: eventProperties.currency,
    amount: eventProperties.amount,
  })
  ga.track('Purchase', eventProperties)
  revcontent.track('Purchase')
}

export const trackCompletedUpgrade = () => {
  ga.track('Click', {
    eventCategory: 'Upgrade',
    eventLabel: 'Completed upgrade',
    eventValue: 0,
  })
}

export const trackPremiumAccountCreated = label => {
  ga.track('Premium account created', {
    eventCategory: 'Signup',
    eventLabel: label,
  })
}

export const trackGiftCardPurchase = () => {
  ga.track('Purchase', {
    eventCategory: 'Gift card',
  })
}

export const trackInitiateCancelSubscriptionClick = ({
  subscriptionDuration,
  purchaseDate,
  expiryTime,
  timeLapsed,
}) => {
  mixpanel.track('Initiate Cancel Premium Subscription Web', {
    'Subscription Length': subscriptionDuration,
    'Subscription Start Date': purchaseDate,
    'Subscription End Date': expiryTime,
    'Subscription Time Lapsed': timeLapsed,
  })
}

export const trackCancelSubscriptionClick = ({
  selectedOption,
  otherAnswer,
  subscriptionDuration,
  purchaseDate,
  expiryTime,
  timeLapsed,
}) => {
  mixpanel.track('Complete Cancellation Premium Subscription Web', {
    'Option Selected': selectedOption,
    'Other Answer': otherAnswer,
    'Subscription Length': subscriptionDuration,
    'Subscription Start Date': purchaseDate,
    'Subscription End Date': expiryTime,
    'Subscription Time Lapsed': timeLapsed,
  })
}
